import { refreshToken, refreshTokenCalledApi } from '@/utils/dataUtils';
import { logout } from '@/utils/authUtils';
import Vue from 'vue';

import { SHOPBY_ACCESSTOKEN, SHOPBY_REFRESHTOKEN } from '@/const/words/cookie';
import ncpApi, { axiosCreator, NCPRequest, NcpXhrError, XhrErrorCode } from 'supporter';
import store from '../store';
import router from '../routes';
import { convertMinutesToMilliseconds } from '@/utils/dateUtils';
import { AxiosRequestConfig } from 'axios';
import { getCookie } from '@/utils/cookieUtils';

export const api = ncpApi.createAdminApi(
  process.env.VUE_APP_API_URL as string,
  SHOPBY_ACCESSTOKEN,
  process.env.VUE_APP_STORAGE_API_URL,
);
export const tokenApi = ncpApi.createAdminApi(
  process.env.VUE_APP_API_URL as string,
  SHOPBY_ACCESSTOKEN,
  process.env.VUE_APP_STORAGE_API_URL,
);
export const commerceApi = ncpApi.createAdminApi(process.env.VUE_APP_COMMERCE_API_URL as string, SHOPBY_ACCESSTOKEN);
export const wceApi = axiosCreator(process.env.VUE_APP_API_URL as string, SHOPBY_ACCESSTOKEN);
export const standardApi = axiosCreator(process.env.VUE_APP_STANDARD_API_URL as string, SHOPBY_ACCESSTOKEN);

let loadingCount = 0;

function addLoading(): void {
  loadingCount++;
  if (store.getters.dimmedAreaSwitchStatus) {
    return;
  }
  store.commit('SET_DIMMEDAREA', true);
}

export function resetLoadingCount() {
  loadingCount = 0;
}

function closeLoading(): void {
  loadingCount > 0 && loadingCount--;

  if (store.getters.dimmedAreaSwitchStatus) {
    return;
  }
  if (loadingCount === 0) {
    store.commit('SET_DIMMEDAREA', false);
  }
}
/* TODO :
 * 백단에서 JSP URL을 VUE URL로 하나씩 바꿔주는 작업을 하고 있는데
 * JSP에서 호출한 VUE popup의 window.callbackName의 callbackName이 달라 문제가 발생한다.
 * 그래서 임시 방편으로 VUE의 특정 팝업 경로를 바꿔주게 되었다.
 * /product/popup/search -> /product/search/popup
 * /member/popup/list -> /member/list/popup
 * /member/popup/target -> /member/target/popup
 * /address/popup -> /address/search/popup
 * 그리고 권한 체크 문제가 발생할 수 있어 headers.ClientLocation에 기존 URL을 임시로 넣어줘야 한다.
 * 그리고 이 모든 것들은 VUE가 JSP를 모두 대체했을때 원상 복구 되어야 한다.
 * 20210223 hslee
 * */
const TEMP_CHANGED_POPUP_URL = {
  '/product/search/popup': '/product/popup/search',
  '/member/list/popup': '/member/popup/list',
  '/member/target/popup': '/member/popup/target',
  '/address/search/popup': '/address/popup',
};

type CustomTimeoutInfo = Record<string, number>;
// key: 엔드포인트
// value: 원하는 타임아웃 설정 값 (convertMinutesToMilliseconds 에 원하는 n 분을 인자로 넘기세요)
const customTimeoutInfo: CustomTimeoutInfo = {
  '/statistics/sales/product': convertMinutesToMilliseconds(5), // 5분
  '/statistics/sales/product/summary': convertMinutesToMilliseconds(5),
  '/statistics/sales/category': convertMinutesToMilliseconds(5),
  '/statistics/sales/category/summary': convertMinutesToMilliseconds(5),
  '/statistics/promotions/additional-discount/summary': convertMinutesToMilliseconds(5),
  '/statistics/promotions/additional-discount': convertMinutesToMilliseconds(5),
  '/statistics/promotions/product-coupons/summary': convertMinutesToMilliseconds(5),
  '/statistics/promotions/product-coupons': convertMinutesToMilliseconds(5),
  '/statistics/sales/period': convertMinutesToMilliseconds(5),
  '/statistics/sales/period/summary': convertMinutesToMilliseconds(5),

  '/order-options/sale-summary-mall': convertMinutesToMilliseconds(5), // 메인 화면 판매현황
  '/settlements': convertMinutesToMilliseconds(5), // 파트너 정산현황
};

// { 특정페이지 pathname : api 호출 url[] }
const preventSpinnerInfo = {
  '/order/list/product-prepare': ['/order-options/mall-delay-summary'],
  '/order/list/delivery-prepare': ['/order-options/naver-delay-summary'],
  '/': ['/claims/summary/delay', '/claims/summary'],
  '/claim/list/cancel': ['/claims/summary/delay'],
  '/claim/list/exchange': ['/claims/summary/delay'],
  '/claim/list/return': ['/claims/summary/delay'],
};

const isRequiredSpinner = (config: AxiosRequestConfig) => {
  return !preventSpinnerInfo[location.pathname]?.includes(config.url);
};

const onRequestFulfilled = async (config: NCPRequest) => {
  const authTokenApi = config.url.includes('auth/token');
  const adminExpire = store.getters['admin/admin']?.exp * 1000;
  const hasRefreshTokenCookie = await getCookie(SHOPBY_REFRESHTOKEN);
  const hasAccessTokenCookie = await getCookie(SHOPBY_ACCESSTOKEN);
  const isExpiredServerAccessToken = new Date(adminExpire) < new Date();

  //at쿠키 & at토큰 만료됐을때 => 401 오류일때
  if ((!hasAccessTokenCookie || isExpiredServerAccessToken) && hasRefreshTokenCookie && !authTokenApi) {
    const refreshFlg = await refreshToken();
    if (!refreshFlg) {
      if (config.customAxiosOption?.on401) {
        config.customAxiosOption?.on401();
      } else {
        router.replace({ name: 'ErrorPageUnauthorized' }); //token 실패
      }
    }
  }

  config.headers.accessToken = getCookie(SHOPBY_ACCESSTOKEN);
  if (!config.headers.accessToken) {
    delete config.headers.accessToken;
  }

  isRequiredSpinner(config) && addLoading();

  // 서버에서 필요한 referer 정보를 client-side에서 가상으로 생성한다.
  const prevPath = store.getters.getPreviousPath;
  config.headers.ClientReferer =
    prevPath === '/' ? document.referrer || window.location.origin : window.location.origin + prevPath;

  if (TEMP_CHANGED_POPUP_URL[window.location.pathname]) {
    config.headers.ClientLocation = window.location.origin + TEMP_CHANGED_POPUP_URL[window.location.pathname];
  } else {
    config.headers.ClientLocation = window.location.origin + window.location.pathname;
  }

  config.timeout = customTimeoutInfo[config.url] ?? convertMinutesToMilliseconds(5); // default 5분

  return config;
};

const onRequestRejected = (error: any) => {
  closeLoading();
  return Promise.reject(error);
};

const onResponseFulfilled = (response: any) => {
  closeLoading();
  refreshTokenCalledApi();
  return response;
};

const onResponseRejected = async (err: NcpXhrError): Promise<NcpXhrError> => {
  closeLoading();

  // 앱 실행의 경우 error처리를 내부에서 하고있음
  if(err.config.url.includes('/auth/token/server-api/code')){
    return Promise.reject(err);
  }

  //NOTE: refreshToken 401 오류
  if (err.code === XhrErrorCode.TOKEN_ERROR) {
    refreshToken().then((refreshFlg: boolean): void => {
      if (!refreshFlg) {
        if (err.config.customAxiosOption?.on401) {
          err.config.customAxiosOption?.on401();
        } else {
          router.replace({ name: 'ErrorPageUnauthorized' }); //token 실패
        }
      }
    });
  }

  if (err.code === XhrErrorCode.FORBIDDEN_ERROR) {
    if (err.config.customAxiosOption?.on403) {
      err.config.customAxiosOption?.on403();
    } else {
      window.location.href = '/nopermission';
    }
  }

  if (err.data.path !== '/products/restock/view' && err.code === XhrErrorCode.NOTFOUND_ERROR) {
    if (err.config.customAxiosOption?.on404) {
      err.config.customAxiosOption?.on404();
    } else {
      window.location.href = '/PageNotFound';
    }
  }

  if (err.code === XhrErrorCode.TIMEOUT_ERROR || err.code === XhrErrorCode.NETWORK_ERROR) {
  }

  if (
    err.data.path !== '/products/restock/view' &&
    err.data.path !== '/apps/1024/installed' &&
    err.code === XhrErrorCode.SERVER_ERROR
  ) {
    // window.location.href = '/errordata';
    alert('서버접속이 원할하지 않습니다. 잠시 후 재시도 해주세요.');
  }

  if (err.code === XhrErrorCode.CLIENT_ERROR) {
    const {
      data: { path, errors },
    } = err;
    if (path === '/admins/service' && errors.length) {
      alert(errors[0].reason);
      return;
    }

    const noalert = [];
    const routerName = (router.app as any)._route.name;
    const KAKAO_SYNC_NOT_INSTALLED_ERROR_CODE = 'AP0004';
    noalert.push('IS0002'); // 인스타그램 토큰 정보 유효하지 않음 에러 코드
    noalert.push(KAKAO_SYNC_NOT_INSTALLED_ERROR_CODE); // 카카오싱크 설치 되지 않음 에러코드
    noalert.push('E0301');
    noalert.push('NCPE0005'); // /order-configs/payments/pg 에러 코드
    noalert.push('NCPE0014'); // 기획전 카테고리 유효성 검사 에러 코드
    if (routerName === 'Login') {
      noalert.push('error.auth.AU1001');
      noalert.push('error.admin.A0003');
      noalert.push('error.auth.E0003');
      noalert.push('error.auth.E0010');
    }
    if (routerName === 'ExternalAccess') {
      noalert.push('error.auth.E0400');
    }

    if (routerName === 'AdminEdit') {
      noalert.push('E0301');
    }
    if (routerName === 'AddProduct') {
      noalert.push('PRDT0001');
    }
    if (routerName === 'EditProduct') {
      noalert.push('PRDT0002');
    }
    if (routerName === 'CopyProduct') {
      noalert.push('PRDT0003');
    }
    if (routerName === 'AddNotice' || routerName === 'EditNotice') {
      noalert.push('F0005');
    }

    if (routerName === 'ConditionRegistration') {
      noalert.push('NCPE0011');
    }

    if (err.data?.errors?.some(({ reason }) => reason.includes('Invalid IP'))) {
      noalert.push(err.data.code);
    }

    // E0701 : IP 불일치 (접속 IP가 변경되었습니다. 다시 로그인해주세요.)
    // E0702 : 강제 로그아웃 (다른 기기에서 로그인되어 현재 세션이 종료되었습니다.)
    if (['E0701', 'E0702'].includes(err.data?.code)) {
      if (!location.pathname.includes('/login')) { // 초기 렌더링 중에는 routerName 가 null 일 수 있음
        alert(err.data.message);
      }
      await logout(true); // skipApi: 이미 토큰이 만료되어 로그아웃 API 호출 불가
      return Promise.reject(err);
    }

    if (routerName === 'ServerExcelDownload') {
      return Promise.reject(err);
    }

    let message = err.data.message || err.data.result.message;
    const errors_ = err.data?.errors || err.data?.result?.errors;
    if (errors_ && errors_.length > 0) {
      message += '\n' + errors_[0].reason;
    }
    const code = err.data.code || err.data.result.code;

    if (message && !noalert.some((item): boolean => item === code)) {
      if (routerName === 'DiscountAddForm') {
        alert(err.data.detail[0].message);
      } else {
        alert(message);
      }
    }
  }

  return Promise.reject(err);
};

api.axios.interceptors.request.use(
  onRequestFulfilled,
  onRequestRejected
);

api.axios.interceptors.response.use(
  onResponseFulfilled,
  onResponseRejected
);
Vue.prototype.$api = api;

commerceApi.axios.interceptors.request.use(
  onRequestFulfilled,
  onRequestRejected
);

commerceApi.axios.interceptors.response.use(
  onResponseFulfilled,
  onResponseRejected
);

wceApi.interceptors.request.use(
  onRequestFulfilled,
  onRequestRejected
);

wceApi.interceptors.response.use(
  onResponseFulfilled,
  onResponseRejected
);

standardApi.interceptors.request.use(
  (config) => {
    /**
     * NOTE: `onResponseFulfilled`에서 admin-api를 위해 header에 accessToken을 추가함
     * standardApi에서는 accessToken 대신 clientId를 사용함
     * 문제는 standardApi에서 accessToken를 받을 경우 401 에러가 발생함
     * 그럼에도 `onResponseFulfilled`를 사용하는 이유는 `onResponseFulfilled`의 다른 로직들을 태우기 위함
     * 따라서 header의 accessToken 제거
     */
    delete config.headers.accessToken;

    config.headers.platform = 'PC';

    return config;
  }
);

standardApi.interceptors.request.use(
  onRequestFulfilled,
  onRequestRejected
);

standardApi.interceptors.response.use(
  onResponseFulfilled,
  onResponseRejected
);
